import React, { useEffect, useState } from "react";

import { Heading } from "@stellar/design-system";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { completeOnboarding } from "@/api/completeOnboarding";
import { SectionHeader } from "@/components/SectionHeader";
import { Routes } from "@/constants/settings";
import { useCreateNewUser } from "@/apiQueries/useCreateNewUser";
import { useRedux } from "@/hooks/useRedux";
import { AppDispatch } from "@/store";
import {
  getOrgInfoAction,
  getOrgLogoAction,
  updateOrgInfoAction,
} from "@/store/ducks/organization";
import { markOnboardingCompletedAction } from "@/store/ducks/userAccount";
import { UserRole } from "@/types";

import { Step1OrgProfile } from "./components/Step1OrgProfile";
import { Step2FundAccount } from "./components/Step2FundAccount";
import {
  Step3InviteTeammates,
  InviteRow,
  newRow,
  canInviteRow,
} from "./components/Step3InviteTeammates";
import { Step4AllSet } from "./components/Step4AllSet";

import "./index.scss";

export const Onboarding = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { userAccount, organization } = useRedux("userAccount", "organization");
  const inviteUser = useCreateNewUser();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const ALLOWED_LOGO_MIME_TYPES = new Set(["image/png", "image/jpeg"]);

  // Step 1
  const [orgName, setOrgName] = useState("");
  const [isOrgNameTouched, setIsOrgNameTouched] = useState(false);
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState("");
  const [language, setLanguage] = useState("en");
  const [logoFile, setLogoFile] = useState<File | undefined>();
  const [logoPreview, setLogoPreview] = useState<string | undefined>();
  const [step1Loading, setStep1Loading] = useState(false);
  const [step1Error, setStep1Error] = useState<string | undefined>();

  // Step 3
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([newRow()]);

  // Step 4
  const [completeError, setCompleteError] = useState<string | undefined>();
  const [isCompleting, setIsCompleting] = useState(false);

  // Prefill org name and logo from existing org data
  useEffect(() => {
    dispatch(getOrgInfoAction());
    dispatch(getOrgLogoAction());
  }, [dispatch]);

  useEffect(() => {
    // Prefill once from org data, but don't override user edits (including clearing the field).
    if (!isOrgNameTouched && organization.data.name && orgName === "") {
      setOrgName(organization.data.name);
    }
  }, [isOrgNameTouched, organization.data.name, orgName]);

  useEffect(() => {
    if (organization.data.language) {
      setLanguage(organization.data.language);
    }
  }, [organization.data.language]);

  useEffect(() => {
    if (organization.data.logo && !logoFile) {
      setLogoPreview(organization.data.logo);
    }
  }, [organization.data.logo, logoFile]);

  // Revoke blob URL when preview changes or component unmounts to avoid memory leak.
  // Guard with startsWith("blob:") so we never revoke the server-side logo URL.
  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // Call completeOnboarding once when step 4 is reached.
  // isCompleting keeps the action buttons disabled until the API call resolves
  // so the user cannot navigate away before isFirstLogin is cleared in Redux.
  useEffect(() => {
    if (step === 4) {
      setIsCompleting(true);
      setCompleteError(undefined);
      completeOnboarding(userAccount.token)
        .then(() => {
          // Clear isFirstLogin in Redux so PrivateRoute no longer forces
          // the user back to onboarding after navigating away.
          dispatch(markOnboardingCompletedAction());
        })
        .catch((err: unknown) => {
          const msg =
            err && typeof err === "object" && "error" in err
              ? String((err as { error: unknown }).error)
              : "Failed to complete onboarding";
          setCompleteError(msg);
        })
        .finally(() => {
          setIsCompleting(false);
        });
    }
  }, [dispatch, step, userAccount.token]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_LOGO_MIME_TYPES.has(file.type)) {
      setLogoFile(undefined);
      setLogoPreview(undefined);
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleStep1Next = async () => {
    setStep1Loading(true);
    setStep1Error(undefined);
    try {
      await dispatch(
        updateOrgInfoAction({
          name: orgName,
          privacyPolicyLink,
          language,
          ...(logoFile ? { logo: logoFile } : {}),
        }),
      ).unwrap();
      setStep(2);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Failed to save organization info";
      setStep1Error(msg);
    } finally {
      setStep1Loading(false);
    }
  };

  const isStep3Loading = inviteRows.some((r) => r.status === "loading");

  const canProceedStep3 =
    !isStep3Loading &&
    inviteRows.some((row) => canInviteRow(row)) &&
    inviteRows.every(
      (row) => canInviteRow(row) || (!row.firstName && !row.lastName && !row.email && !row.role),
    );

  const handleStep3Next = async () => {
    const rowsToInvite = inviteRows.filter((row) => canInviteRow(row));

    if (rowsToInvite.length === 0) {
      setStep(4);
      return;
    }

    setInviteRows((prev) =>
      prev.map((r) =>
        rowsToInvite.find((ri) => ri.id === r.id) ? { ...r, status: "loading" } : r,
      ),
    );

    const results = await Promise.all(
      rowsToInvite.map(async (row) => {
        try {
          await inviteUser.mutateAsync({
            first_name: row.firstName,
            last_name: row.lastName,
            email: row.email,
            role: row.role as UserRole,
          });
          return { id: row.id, succeeded: true };
        } catch (err: unknown) {
          const msg =
            err && typeof err === "object" && "message" in err
              ? String((err as { message: unknown }).message)
              : "Failed to invite user";
          return { id: row.id, succeeded: false, errorMsg: msg };
        }
      }),
    );

    const allSucceeded = results.every((r) => r.succeeded);

    setInviteRows((prev) => {
      const remaining = prev
        .map((r) => {
          const result = results.find((res) => res.id === r.id);
          if (!result) return r;
          if (result.succeeded) return null;
          return { ...r, status: "error" as const, errorMsg: result.errorMsg };
        })
        .filter((r): r is InviteRow => r !== null);
      return remaining.length > 0 ? remaining : [newRow()];
    });

    if (allSucceeded) setStep(4);
  };

  const { distributionAccountPublicKey } = organization.data;

  return (
    <div className="OnboardingPage">
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm" className="OnboardingPage__title">
              Welcome to Stellar Disbursement Platform
            </Heading>
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      <div className="Onboarding">
        {step === 1 && (
          <Step1OrgProfile
            orgName={orgName}
            setOrgName={(value) => {
              setIsOrgNameTouched(true);
              setOrgName(value);
            }}
            privacyPolicyLink={privacyPolicyLink}
            setPrivacyPolicyLink={setPrivacyPolicyLink}
            language={language}
            setLanguage={setLanguage}
            logoPreview={logoPreview}
            onLogoChange={handleLogoChange}
            loading={step1Loading}
            error={step1Error}
            onNext={handleStep1Next}
          />
        )}
        {step === 2 && (
          <Step2FundAccount
            distributionAccountPublicKey={distributionAccountPublicKey}
            onNext={() => setStep(3)}
            onSkip={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <Step3InviteTeammates
            inviteRows={inviteRows}
            setInviteRows={setInviteRows}
            isLoading={isStep3Loading}
            canProceed={canProceedStep3}
            onNext={handleStep3Next}
            onSkip={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <Step4AllSet
            completeError={completeError}
            isCompleting={isCompleting}
            onStartDisbursement={() => navigate(Routes.DISBURSEMENT_NEW, { replace: true })}
            onExplore={() => navigate(Routes.HOME, { replace: true })}
          />
        )}
      </div>
    </div>
  );
};
